/**
 * claimsort.mjs — sort a real claim into computed, quoted, admitted, or none of the three.
 *
 * FOUNDER, 2026-08-15: "i want claude code chapters to deep dive" → chapter 16's instrument.
 *
 * ── THE CHAPTER TEACHES A SORTABLE SKILL, WHICH IS RARE ───────────────────────────────────────
 * §3: "Every claim in a technical answer is one of three things" — a computation you can re-run, a
 * quotation from a named source, or an abstention. "Anything that is none of those three is a
 * fourth kind, and it is the one to fear."
 *
 * Most of this ladder teaches judgement, which an instrument can only illustrate. This chapter
 * teaches a CLASSIFICATION, and a classification can be practised. So the lab hands the learner
 * claims and asks which kind each one is.
 *
 * ── EVERY CLAIM IS REAL, AND FOUR OF THEM ARE FABRICATIONS THIS PROJECT SHIPPED ───────────────
 * The temptation is to write plausible examples. That would teach sorting invented claims, which is
 * a different and easier skill — an invented "bad" example is written to look bad.
 *
 * So every claim below was actually made during the building of this academy, and its `kind` is
 * what it actually was. The four marked `ungrounded` are fabrications the assistant produced and a
 * gate caught: the permission row that stated behaviour the help text does not state, the plausible
 * mode that does not exist, the count that flattered by double-counting, the law tested only where
 * it could not fail. A learner sorting those is sorting the real thing, in its natural habitat,
 * where it looked entirely reasonable at the time.
 *
 * ── AND THE FOURTH KIND IS NOT ALWAYS WRONG ───────────────────────────────────────────────────
 * §3: "It may well be correct — models are right about a great deal — but you have no way to
 * establish that from the text." The lab must not teach that ungrounded means false. It means
 * unestablished, which is a different and more useful thing to notice.
 */

export const SOURCE = 'CLAUDECODE101 chapter 16 §3 "The three kinds of answer"';

/** The four kinds, in the chapter's own words. */
export const KINDS = Object.freeze([
  { id: 'computed', name: 'A computation',
    says: 'Derived by a rule you can re-run.',
    check: 'Do the arithmetic yourself.' },
  { id: 'quoted', name: 'A quotation',
    says: 'Taken from a named source.',
    check: 'Open the source and look.' },
  { id: 'admitted', name: 'An abstention',
    says: 'Saying you do not know.',
    check: 'Nothing to check — it is already telling you it has no answer.' },
  { id: 'ungrounded', name: 'None of the three',
    says: 'No derivation, no source, no admission. The one to fear.',
    check: 'There is no cheap check. That IS the finding.' },
]);

/**
 * Real claims from the building of this academy. `kind` is what each one actually was.
 *
 * The ungrounded four are not inventions for the exercise. They shipped, briefly, and a mechanical
 * check caught each one — which is the chapter's whole argument arriving as evidence rather than
 * as advice.
 */
export const CLAIMS = Object.freeze([
  { id: 'rate', kind: 'computed',
    text: '36.0 tool calls per prompt.',
    why: '576 divided by 16. Both numbers were counted from a transcript, and you can do the division.',
    check: 'Re-count the transcript and divide again.' },

  { id: 'modes', kind: 'quoted',
    text: 'The tool offers six permission modes: acceptEdits, auto, bypassPermissions, manual, dontAsk, plan.',
    why: 'Read from `claude --help`. Not a description of the tool — the tool\'s own output.',
    check: 'Run `claude --help` and look at the --permission-mode choices.' },

  { id: 'autodelete', kind: 'admitted',
    text: 'What does `auto` do when asked to delete a directory that is not in version control? Not known from the help text.',
    why: 'The tool says that mode decides for itself when to ask, and does not state the rule. Saying so is the honest answer.',
    check: 'Nothing to check. It is telling you where its knowledge ends.' },

  /* ── THE FOUR THAT WERE ACTUALLY FABRICATED ─────────────────────────────────────────────── */
  { id: 'autoproceeds', kind: 'ungrounded', shipped: true,
    text: 'Under `auto`, a delete of an unversioned directory proceeds without asking.',
    why: 'This was in the chapter 4 lab. It is a definite claim about behaviour the help text does not state — reasonable-sounding, because a mode called auto plausibly proceeds automatically. That plausibility is what made it hard to see.',
    check: 'Ask which source says so. There is none — which is why the row now reads "depends".',
    caught_by: 'Writing down, ahead of time, that the lab may only state behaviour the tool documents.' },

  { id: 'readonly', kind: 'ungrounded',
    text: 'The permission layer offers a `readOnly` mode for auditing.',
    why: 'More helpful-sounding than the truth, and exactly what a thoughtful person expects a well-designed tool to have. It does not exist.',
    check: 'Run `claude --help`. Six modes, and readOnly is not among them.',
    caught_by: 'A gate that matches every mode named in the prose against the tool\'s own output.' },

  { id: 'doublecount', kind: 'ungrounded', shipped: true,
    text: 'This app emits 116 computed values and 219 citations.',
    why: 'A self-audit that counted every file twice. The figures looked entirely reasonable, and they flattered.',
    check: 'Count again, independently. The real figures were 77 and 127.',
    caught_by: 'A gate that re-took the measurement instead of trusting the recorded one.' },

  { id: 'vacuous', kind: 'ungrounded', shipped: true,
    text: 'A stricter supervision tier does not change what the AI proposes — verified by a passing check.',
    why: 'The check was real and it passed. It compared the two tiers only on the case with no fault injected, so it never exercised the branch it existed to protect. A green check is a claim like any other.',
    check: 'Break the thing the check protects and watch whether it goes red.',
    caught_by: 'Deliberately introducing the exact fault the law was written for.' },

  /* ── THE WORD THAT DID NOT SURVIVE ITS OWN CHECK ─────────────────────────────────────────
     This read "46 ACADEMY gates" while its check counted every gate file — 47 against 51, because
     four maritime gates are excluded from the academy sweep and the command does not know that.
     A claim that LOOKS computed, but whose stated check does not produce its stated number, is the
     fourth kind wearing the first kind's clothes. Caught by a gate that ran the check instead of
     reading it. The claim and the command now describe the same thing. */
  { id: 'gates', kind: 'computed',
    text: 'This project runs 97 gate files.',
    why: 'Counted by listing the files that match a pattern. The number moves whenever a gate is added, which is why it is computed on demand rather than remembered — a recorded count of a growing thing is stale by the next commit.',
    check: 'ls slm-tutor/gate-*.mjs | wc -l — and read the claim carefully: it says gate FILES, not academy gates, because that command cannot tell the difference.' },

  { id: 'price', kind: 'admitted',
    text: 'What does a tool call cost? Not stated here, because it varies by model and tool and this course cannot check it.',
    why: 'CodeAI101 gives no per-call rate deliberately. Quoting one would be inventing a commercial fact.',
    check: 'Nothing to check. The silence is the answer.' },

  { id: 'shelf', kind: 'quoted',
    text: 'CM chapter 9 is "Medical Care on Board".',
    why: 'Read from the CM ladder file, which is the declaration of what that ladder teaches.',
    check: 'Open _cm-ladder.json and find chapter 9.' },
]);

/** Was the sort right? No score is kept — see the lab for why. */
export const sortOf = (id) => CLAIMS.find((c) => c.id === id) || null;
export const isRight = (id, guess) => sortOf(id)?.kind === guess;

/** The ones that actually shipped before a check caught them. */
export const shipped = () => CLAIMS.filter((c) => c.shipped);
export const ungrounded = () => CLAIMS.filter((c) => c.kind === 'ungrounded');

/**
 * UNGROUNDED IS NOT THE SAME AS FALSE, and the lab says so wherever it says anything.
 *
 * §3 is explicit: a fourth-kind claim "may well be correct — models are right about a great deal —
 * but you have no way to establish that from the text". A learner who leaves believing ungrounded
 * means wrong will discard true things and, worse, will trust the next confident sentence that
 * happens to be sourced.
 */
export const NOT_THE_SAME_AS_FALSE = 'None of these four is a lie. Each may even be true — the double-count was arithmetic, not invention, and a readOnly mode is the sort of thing a tool might well have. Ungrounded means you cannot establish it from what is in front of you, which is a different finding and a more useful one.';
export const WHY_REAL_CLAIMS = 'Every claim here was actually made while building this academy, and the four marked ungrounded shipped before a mechanical check caught them. Invented examples would teach sorting invented claims — an easier skill, because a made-up bad example is written to look bad.';
export const THE_HABIT = 'The move is not to distrust everything. It is to sort by consequence: which of these claims would cost me something if wrong, and what is the cheapest mechanical check for those.';
